<template>
	<v-container fluid>

		<v-skeleton-loader v-if="isLoading" type="table" />

		<v-row v-if="!isLoading && !errorMessage" class="mx-4">
			<v-col cols="12">
				<v-tabs v-model="tab">
					<v-tab>{{ $t('wkt.admin.complaintconfig.tabs.general-config').toUpperCase() }}</v-tab>
					<v-tab>{{ $t('wkt.admin.complaintconfig.tabs.general-info').toUpperCase() }}</v-tab>
					<v-tab>{{ $t('wkt.admin.complaintconfig.tabs.irs-policy').toUpperCase() }}</v-tab>
					<v-tab>{{ $t('wkt.admin.complaintconfig.tabs.management-procedure').toUpperCase() }}</v-tab>
					<v-tab>{{ $t('wkt.admin.complaintconfig.tabs.irs-privacy-policy').toUpperCase() }}</v-tab>
				</v-tabs>

				<v-tabs-items v-model="tab" style="padding: 0 20px;">
					<v-tab-item eager>
						<v-form ref="form1">
							<v-row style="margin-top: 20px;">
								<v-col cols="6" class="pb-0 pl-4">
									<v-switch
										v-model="company.moduleComplaintEnabled"
										flat
										:label="$t('wkt.admin.complaintconfig.module_complaint_enabled')"
										></v-switch>
								</v-col>								
								<v-col cols="6">
									<v-text-field
									v-model="company.complaintExpirationDays"
									:rules="[validationRules.required, validationRules.integer, validationRules.positive]"
									:label="$t('wkt.admin.complaintconfig.complaint_expiration_days')"
									autocomplete="none"
									type="number"
									step="1"
									oninput="if(this.value < 0) this.value = 0;"
									@keypress="isNumber($event)"
									/>
								</v-col>
							</v-row>
							<v-row style="margin-top: 20px;">
								<v-col cols="6">
									<v-autocomplete
										v-model="company.complaintManagerId"
										:rules="[validationRules.required]"
										:items="employees"
										item-value="id"
										:filter="filterEmployees"
										menu-props="bottom, offsetY"
										:label="$t('wkt.admin.complaintconfig.complaint_manager')"
										:placeholder="$t('wkt.admin.common.filters.employee_placeholder')">
										<template v-slot:selection="{item}">
											{{ `${getEmployeeFullname(item)}${!item.isEnabled ? ` (${$t('wkt.admin.edit-employee-window.disabled')})` : ''}` }}
										</template>
										<template v-slot:item="{item}">
											{{ `${getEmployeeFullname(item)}${!item.isEnabled ? ` (${$t('wkt.admin.edit-employee-window.disabled')})` : ''}` }}
										</template>
									</v-autocomplete>
								</v-col>
								<v-col cols="6">
									<v-text-field
										v-model="company.complaintNotificationEmail"
										:rules="[validationRules.email]"
										:label="$t('wkt.admin.complaintconfig.complaint_notification_email')"
										autocomplete="none" />
								</v-col>
								<v-col v-if="company.complaintAccessToken" cols="12">
									<span class="d-block">{{ $t('wkt.admin.complaintconfig.external_access_url') }}</span>
									<a ref="externalUrl" :href="accessUrl" target="_blank">{{ accessUrl }}</a>
									<v-icon class="ml-4" style="color: #185EA9;" @click="copyUrlToClipboard()">content_copy</v-icon>
								</v-col>
							</v-row>
						</v-form>
					</v-tab-item> 
					<v-tab-item eager>
						<v-form ref="form2">
							<v-row style="margin-top: 20px;">
								<v-col cols="12">
									<tiptap-vuetify 
                                       v-model="externalContent.generalInfo"
                                       :extensions="extensions" 
                                       :card-props="{ flat: true }"
										style="height: calc(100vh - 400px); overflow-y: auto; border: 1px solid #DDDDDD; border-radius: 6px;" />
									<small v-if="errorGeneralInfo" class="red--text">{{ $t('wkt.admin.common.validation-utils.required_field') }}</small>
								</v-col>
							</v-row>
						</v-form>
					</v-tab-item>  
					<v-tab-item eager>
						<v-form ref="form3">
							<v-row style="margin-top: 20px;">
								<v-col cols="12">
									<tiptap-vuetify 
                                       v-model="externalContent.policy"
                                       :extensions="extensions" 
                                       :card-props="{ flat: true }"
										style="height: calc(100vh - 400px); overflow-y: auto; border: 1px solid #DDDDDD; border-radius: 6px;" />
									<small v-if="errorPolicy" class="red--text">{{ $t('wkt.admin.common.validation-utils.required_field') }}</small>
								</v-col>
							</v-row>
						</v-form>
					</v-tab-item>  
					<v-tab-item eager>
						<v-form ref="form4">
							<v-row style="margin-top: 20px;">
								<v-col cols="12">
									<tiptap-vuetify 
                                       v-model="externalContent.procedures"
                                       :extensions="extensions" 
                                       :card-props="{ flat: true }"
										style="height: calc(100vh - 400px); overflow-y: auto; border: 1px solid #DDDDDD; border-radius: 6px;" />
									<small v-if="errorProcedures" class="red--text">{{ $t('wkt.admin.common.validation-utils.required_field') }}</small>
								</v-col>
							</v-row>
						</v-form>
					</v-tab-item>
					<v-tab-item eager>
						<v-form ref="form5">
							<v-row style="margin-top: 20px;">
								<v-col cols="12">
									<tiptap-vuetify 
                                       v-model="externalContent.privacyPolicy"
                                       :extensions="extensions" 
                                       :card-props="{ flat: true }"
										style="height: calc(100vh - 400px); overflow-y: auto; border: 1px solid #DDDDDD; border-radius: 6px;" />
									<small v-if="errorPrivacyPolicy" class="red--text">{{ $t('wkt.admin.common.validation-utils.required_field') }}</small>
								</v-col>
							</v-row>
						</v-form>
					</v-tab-item>
				</v-tabs-items>
			</v-col>
			<v-col v-if="!isLoading && !errorMessage" cols="12" class="text-right">
				<v-btn color="#185EA9" text :disabled="disableOkButton" @click="ok()" style="font-size: 0.9em; font-weight: 400;">{{ $t('wkt.admin.common.dialog-buttons.ok').toUpperCase() }}</v-btn>
				<v-btn text @click="cancel()" style="font-size: 0.9em; font-weight: 400;">{{ $t('wkt.admin.common.dialog-buttons.cancel').toUpperCase() }}</v-btn>
			</v-col>
		</v-row>

		<ConfirmEnableComplaintModuleDialog ref="dlgConfirm" />

		<!-- INICIO BLOQUE MENSAJE ESTADO PETICIONES -->
		<v-snackbar
		v-model="showAlert"
		:timeout="alertTimeout"
		:color="alertColor">
			<span style="color: #ffffff;">{{alertText}}</span>
			<v-btn text @click="showAlert=false">
			<span style="color: #ffffff;">{{ $t('wkt.admin.common.dialog-buttons.close') }}</span>
			</v-btn>
		</v-snackbar>
		<!-- FIN BLOQUE ESTADO PETICIONES -->

	</v-container>
</template>

<script>
import BaseModule from '../common/BaseModule.js'
import api from '../../api/worktime'
import gwt from '../../api/gwt'
import { TiptapVuetify, Bold, Italic, Strike, Underline, Code, BulletList, OrderedList, ListItem, Link, Blockquote, History } from 'tiptap-vuetify'
import { v4 as uuidv4 } from 'uuid';
import ConfirmEnableComplaintModuleDialog from './ConfirmEnableComplaintModuleDialog.vue';

export default {
  name: 'ComplaintConfigModule',
  mixins: [BaseModule],
  components: {
	TiptapVuetify, ConfirmEnableComplaintModuleDialog
  },
  data () {
    return { 
        isLoading: false,
		errorMessage: null,
		tab: 0,
		oldCompany: {},
		company: {},
		oldExternalContent: {},
		externalContent: {},
		moduleEnabled: false,
		isSuperAdmin: false,
		profile: null,
		employees: [],
		accessUrl: null,
		showAlert: false,
		alertColor: 'error',
		alertText: '',
		alertTimeout: 3000,
		extensions: [
			History,
			Blockquote,
			Link,
			Underline,
			Strike,
			Italic,
			ListItem,
			BulletList,
			OrderedList,
			Bold,
			Code
		],
		errorGeneralInfo: false,
		errorPolicy: false,
		errorProcedures: false,
		errorPrivacyPolicy: false,
      }
    },
	computed: {
		disableOkButton() {
			const isBusinessOrCorporate = this.company.licenseType > 0;
			const profileHasRwPermission = this.profile && this.profile.moduleComplaintConfig === 2;

			return !isBusinessOrCorporate || !profileHasRwPermission;
		}
	},
		methods: {
		isNumber: function(event) {
			const keyCode = event.keyCode || event.which;
			if (!((keyCode >= 48 && keyCode <= 57) || keyCode === 8)) {
			event.preventDefault();
				}
				},
		doShowAlert: function(payload) {
			this.alertText = payload.text;
			this.alertColor = payload.color;
			this.alertTimeout = payload.timeout;
			this.showAlert = true;
		},	
		copyUrlToClipboard() {
			var element = document.createElement("textarea");
			element.value = this.accessUrl;
			
			// avoid scrolling to bottom
			element.style.top = '0';
			element.style.left = '0';
			element.style.position = 'fixed';

			document.body.appendChild(element);
			element.focus();
			element.select();

			document.execCommand('copy');

			this.doShowAlert({ text: this.$t('wkt.admin.complaintconfig.link_copied_to_clipboard'), color: 'success', timeout: 6000 });

			setTimeout(function() {
				document.body.removeChild(element);
			}, 1000);
		},
		validate() {
			let isValid = true;
		
			this.errorGeneralInfo = false;
			this.errorPolicy = false;
			this.errorProcedures = false;
			this.errorPrivacyPolicy = false;

			if (isValid && !this.$refs.form1.validate()) {
				if (this.tab != 0) {
					this.tab = 0;
				}
				isValid = false;
			}

			if (isValid && (!this.externalContent.generalInfo || this.externalContent.generalInfo === '<p></p>')) {
				if (this.tab != 1) {
					this.tab = 1;
				}
				this.errorGeneralInfo = true;
				isValid = false;
			}

			if (isValid && (!this.externalContent.policy || this.externalContent.policy === '<p></p>')) {
				if (this.tab != 2) {
					this.tab = 2;
				}
				this.errorPolicy = true;
				isValid = false;
			}

			if (isValid && (!this.externalContent.procedures || this.externalContent.procedures === '<p></p>')) {
				if (this.tab != 3) {
					this.tab = 3;
				}
				this.errorProcedures = true;
				isValid = false;
			}

			if (isValid && (!this.externalContent.privacyPolicy || this.externalContent.privacyPolicy === '<p></p>')) {
				if (this.tab != 4) {
					this.tab = 4;
				}
				this.errorPrivacyPolicy = true;
				isValid = false;
			}

			return isValid;
		},
		hasChanges() {
			const moduleEnabledChanged = this.company.moduleComplaintEnabled !== this.oldCompany.moduleComplaintEnabled;
			const expirationDaysChanged = this.company.complaintExpirationDays !== this.oldCompany.complaintExpirationDays;
			const managerIdChanged = this.company.complaintManagerId !== this.oldCompany.complaintManagerId;
			const notificationEmailChanged = this.company.complaintNotificationEmail !== this.oldCompany.complaintNotificationEmail;
			
			const generalInfoChanged = this.externalContent.generalInfo !== this.oldExternalContent.generalInfo;
			const policyChanged = this.externalContent.policy !== this.oldExternalContent.policy;
			const proceduresChanged = this.externalContent.procedures !== this.oldExternalContent.procedures;
			const privacyPolicyChanged = this.externalContent.privacyPolicy !== this.oldExternalContent.privacyPolicy;
			
			return moduleEnabledChanged || 
				expirationDaysChanged || 
				managerIdChanged || 
				notificationEmailChanged ||
				generalInfoChanged ||
				policyChanged ||
				proceduresChanged ||
				privacyPolicyChanged;
		},		
		ok() {
			if (!this.hasChanges() || !this.validate()) {
				return;
			}

			if (!this.moduleEnabled && this.company.moduleComplaintEnabled) {
				this.$refs.dlgConfirm.open(() => {
					this.updateCompany();
				}, () => {					
					this.cancel();
				});
				return;
			}
					
			this.updateCompany();
		},
		updateCompany() {
			this.isLoading = true;

			if (!this.company.complaintAccessToken) {
				this.company.complaintAccessToken = uuidv4().toString();
			}

			api.updateMyCompany(this.company,
				res => {
					this.isLoading = false;
					
					if (!res.rsOK) {
						this.doShowAlert({ text: res.message, color: 'error', timeout: 6000 });
						return;
					}

					gwt.setSessionCompany(JSON.stringify(this.company));

					this.moduleEnabled = this.company.moduleComplaintEnabled;

					const appUrl = gwt.getConfigurations().find(x => x.key === 'common.complaint-app.url');

					if (appUrl && this.company.complaintAccessToken) {
						this.accessUrl = `${appUrl.value}${this.company.complaintAccessToken}`;
					} else {
						this.accessUrl = null;
					}

					if (this.company.moduleComplaintEnabled) {
						this.updateComplaintTerm();
					}

					this.updateComplaintExternalContent();
					
					this.oldCompany = { ...this.company };
				}, 
				error => {
					console.log(error);
					this.isLoading = false;
					this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
				}
			);
		},
		updateComplaintTerm() {
			this.isLoading = true;

			api.updateComplaintTerm(this.company.id,
				() => {
					this.isLoading = false;
				}, 
				error => {
					console.log(error);
					this.isLoading = false;
					this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
				}
			);
		},
		updateComplaintExternalContent() {
			this.isLoading = true;

			api.updateComplaintExternalContent(this.externalContent,
				res => {
					this.isLoading = false;
					
					if (!res) {
						this.doShowAlert({ text: this.$t('wkt.admin.common.error.save_server'), color: 'error', timeout: 6000 });
						return;
					}
					
					this.oldExternalContent = { ...this.externalContent };

					this.doShowAlert({ text: this.$t('wkt.admin.general-customize.data_saved_correctly'), color: 'success', timeout: 6000 });
				}, 
				error => {
					console.log(error);
					this.isLoading = false;
					this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
				}
			);
		},
		cancel() {
			this.company = { ...this.oldCompany };
			this.externalContent = { ...this.oldExternalContent };
			this.moduleEnabled = this.company.moduleComplaintEnabled;
		},
		onReveal() {      
			if (!gwt.getSessionInformation()) {
				setTimeout(this.onReveal, 1000);
				return;
			}

			this.getEmployees();

			this.company = gwt.getSessionInformation().company;
			this.isSuperAdmin = gwt.getSessionInformation().employee.isSuperAdmin;
			this.profile = gwt.getSessionProfile();

			this.moduleEnabled = this.company.moduleComplaintEnabled;

			this.oldCompany = { ...this.company };

			const appUrl = gwt.getConfigurations().find(x => x.key === 'common.complaint-app.url');

			if (appUrl && this.company.complaintAccessToken) {
				this.accessUrl = `${appUrl.value}${this.company.complaintAccessToken}`;
			} else {
				this.accessUrl = null;
			}
			
			this.getComplaintExternalContent();

			if (this.$refs.form1) {
				this.$refs.form1.resetValidation();
			}

			if (this.$refs.form2) {
				this.$refs.form2.resetValidation();
			}

			if (this.$refs.form3) {
				this.$refs.form3.resetValidation();
			}

			if (this.$refs.form4) {
				this.$refs.form4.resetValidation();
			}

			if (this.$refs.form5) {
				this.$refs.form5.resetValidation();
			}
		
			this.errorGeneralInfo = false;
			this.errorPolicy = false;
			this.errorProcedures = false;
			this.errorPrivacyPolicy = false;
		},
		getEmployees() {
			this.isLoading = true;
			this.employees = [];

			api.getStaff(
				data => {
					this.isLoading = false;
					this.getAllEmployees(data);
				}, 
				error => {
					console.log(error);
					this.isLoading = false;
					this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
				}
			);
		},
		getAllEmployees(departments) {
			for (let i = 0; i < departments.length; i++) {
				this.employees = [...this.employees, ...departments[i].employees];
			}

			this.employees.sort((a,b) => {
				const fullnameA = this.getEmployeeFullname(a).toLowerCase();
				const fullnameB = this.getEmployeeFullname(b).toLowerCase();
				if (fullnameA == fullnameB) return 0;
				if (fullnameA > fullnameB) return 1;
				return -1;
			});
		},
		// eslint-disable-next-line no-unused-vars
		filterEmployees(item, queryText, itemText) {
			return item.firstname.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1 || 
				item.lastname.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1;
		},
		getComplaintExternalContent() {
			this.isLoading = true;
			this.externalContent = {};
			this.oldExternalContent = {};

			api.getComplaintExternalContentByCompanyId(this.company.id,
				res => {
					this.isLoading = false;					
					this.externalContent = res;
					this.oldExternalContent = { ...this.externalContent };
				}, 
				error => {
					console.log(error);
					this.isLoading = false;
					this.doShowAlert({ text: error.message, color: 'error', timeout: 3000 });
				}
			);
		},
	},
}
</script>

<style scoped>
</style>

